import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const navItems = [
  { name: 'Home', path: '/' },
  { name: 'Inventory', path: '/inventory' },
  { name: 'Financing', path: '/financing' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', vehicleInterest: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Schema.org JSON-LD injection
  useEffect(() => {
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://paramountautosusa.com';
    
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          "@id": `${baseUrl}/contact#webpage`,
          "url": `${baseUrl}/contact`,
          "name": "Contact Paramount Motors - Get In Touch",
          "description": "Contact Paramount Motors for sales inquiries, test drives, financing questions, or service appointments. We respond within 2 hours during business hours.",
          "isPartOf": {
            "@id": `${baseUrl}/#website`
          }
        },
        {
          "@type": "AutoDealer",
          "@id": `${baseUrl}/#organization`,
          "name": "Paramount Motors",
          "url": baseUrl,
          "logo": "https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/ff8f99c2c3d017ecd9846c9b47aca342.png",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Miami",
            "addressLocality": "Miami",
            "addressRegion": "FL",
            "addressCountry": "US"
          },
          "telephone": "+1-305-000-0000",
          "email": "info@paramountautosusa.com",
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "19:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Saturday",
              "opens": "09:00",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Sunday",
              "opens": "11:00",
              "closes": "17:00"
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const body = new URLSearchParams(formData as unknown as Record<string, string>).toString();
      const res = await fetch('https://readdy.ai/api/form/d6mhd0aqoe30lj0v8btg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      setSubmitStatus(res.ok ? 'success' : 'error');
      if (res.ok) setFormData({ firstName: '', lastName: '', email: '', phone: '', vehicleInterest: '', subject: '', message: '' });
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const inputClass = 'w-full px-4 py-3.5 min-h-[48px] bg-white border border-[#d1c9b8] focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20 rounded-md text-base text-[#1a1a1a] placeholder-[#b0a898] focus:outline-none transition-all';
  const labelClass = 'block text-xs font-semibold text-[#4a4035] mb-2 tracking-wider uppercase';

  return (
    <div className="min-h-screen bg-[#0d0d0d]">
      <title>Contact Us | Paramount Autos USA – Miami, FL Dealership</title>
      <meta name="description" content="Contact Paramount Autos USA in Miami, FL. Reach our team for sales inquiries, test drives, financing, or service. We respond within 2 hours. Serving Miami and all of the USA." />
      <link rel="canonical" href="https://paramountautosusa.com/contact" />

      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-[#0d0d0d] ${isScrolled ? 'shadow-lg shadow-black/30 border-b border-[#C9A84C]/20 py-3 sm:py-4' : 'py-4 sm:py-6'}`}>
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/7d1f372df28459c5713c0f3e160d8f18.png"
                alt="Paramount Motors Logo"
                style={{ height: '5rem', width: 'auto' }}
                className="object-contain"
              />
            </Link>
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <Link key={item.name} to={item.path} className={`text-xs font-medium tracking-widest uppercase transition-all cursor-pointer relative ${item.name === 'Contact' ? 'text-[#C9A84C]' : 'text-white/60 hover:text-[#C9A84C]'}`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {item.name}
                  {item.name === 'Contact' && <span className="absolute -bottom-1 left-0 w-full h-px bg-[#C9A84C]"></span>}
                </Link>
              ))}
            </div>
            <Link to="/financing" className="hidden lg:block px-6 py-2.5 rounded-sm text-xs font-bold tracking-widest uppercase bg-[#C9A84C] text-[#0d0d0d] hover:bg-[#e0bc5e] transition-all cursor-pointer whitespace-nowrap" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Financing
            </Link>
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden relative z-50 w-10 h-10 flex items-center justify-center cursor-pointer" style={{ color: '#C9A84C' }}>
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} style={{ color: '#C9A84C' }}></i>
            </button>
          </div>
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4">
              <div className="flex flex-col gap-4">
                {navItems.map((item) => (
                  <Link key={item.name} to={item.path} onClick={() => setIsMobileMenuOpen(false)} className="text-xs font-medium tracking-widest uppercase text-white/60 hover:text-[#C9A84C] transition-all cursor-pointer" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.name}</Link>
                ))}
                <Link to="/financing" onClick={() => setIsMobileMenuOpen(false)} className="mt-1 px-6 py-3 rounded-sm text-xs font-bold tracking-widest uppercase bg-[#C9A84C] text-[#0d0d0d] cursor-pointer whitespace-nowrap text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>Financing</Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero — compact on mobile */}
      <section className="relative h-36 sm:h-56 lg:h-64 flex items-end justify-start overflow-hidden pt-16 sm:pt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a6361220f9cffcdb29b1c8edb4450efa.jpg"
            alt="Contact Paramount Motors"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/90 via-[#0d0d0d]/60 to-[#0d0d0d]/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 px-4 sm:px-8 lg:px-16 pb-5 sm:pb-8 lg:pb-10">
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-3">
            <div className="h-px w-6 sm:w-8 bg-[#C9A84C]"></div>
            <span className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Get In Touch</span>
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Contact Us</h1>
        </div>
      </section>

      {/* Quick contact info bar */}
      <section className="bg-[#111111] border-b border-white/5 py-4 sm:py-5 px-4 sm:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10 lg:gap-16">
          <a href="tel:+13050000000" className="flex items-center gap-2.5 cursor-pointer group">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/30">
              <i className="ri-phone-line text-[#C9A84C] text-sm"></i>
            </div>
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-widest" style={{ fontFamily: 'Montserrat, sans-serif' }}>Call Us</p>
              <p className="text-xs text-white/80 group-hover:text-[#C9A84C] transition-colors font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>(305) 000-0000</p>
            </div>
          </a>
          <a href="mailto:info@paramountautosusa.com" className="flex items-center gap-2.5 cursor-pointer group">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/30">
              <i className="ri-mail-line text-[#C9A84C] text-sm"></i>
            </div>
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-widest" style={{ fontFamily: 'Montserrat, sans-serif' }}>Email</p>
              <p className="text-xs text-white/80 group-hover:text-[#C9A84C] transition-colors font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>info@paramountautosusa.com</p>
            </div>
          </a>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/30">
              <i className="ri-map-pin-line text-[#C9A84C] text-sm"></i>
            </div>
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-widest" style={{ fontFamily: 'Montserrat, sans-serif' }}>Location</p>
              <p className="text-xs text-white/80 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>Miami, Florida — Serving All of USA</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="py-8 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-12">

          {/* Form — takes 3/5 on desktop, full width on mobile */}
          <div className="lg:col-span-3 bg-white border border-[#e0d8cc] p-5 sm:p-8 lg:p-10 rounded-lg shadow-lg">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="h-px w-6 sm:w-8 bg-[#C9A84C]"></div>
              <span className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Send a Message</span>
              <div className="h-px w-6 sm:w-8 bg-[#C9A84C]"></div>
            </div>
            <h2 className="text-xl sm:text-3xl font-bold text-[#1a1a1a] mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>How Can We Help?</h2>
            <p
              className="text-[#6b5e4e] max-w-2xl text-sm leading-relaxed mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
            >
              Based in <strong className="text-[#C9A84C] font-semibold">Miami, Florida</strong> and serving customers across the entire USA. Whether you're local or buying remotely, our team is ready to help. We respond within 2 hours during business hours.
            </p>

            {submitStatus === 'success' && (
              <div className="mb-5 p-3 sm:p-4 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-md flex items-center gap-2 sm:gap-3">
                <i className="ri-checkbox-circle-fill text-[#C9A84C] text-xl flex-shrink-0"></i>
                <p className="text-[#4a4035] text-xs font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>Message sent! We'll be in touch shortly.</p>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mb-5 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 sm:gap-3">
                <i className="ri-error-warning-fill text-red-500 text-xl flex-shrink-0"></i>
                <p className="text-red-600 text-xs font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>Something went wrong. Please try again.</p>
              </div>
            )}

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              {/* First + Last */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>First Name</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required className={inputClass} placeholder="John" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                </div>
                <div>
                  <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Last Name</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required className={inputClass} placeholder="Doe" style={{ fontFamily: 'Montserrat, sans-serif' }} />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className={inputClass} placeholder="john@example.com" style={{ fontFamily: 'Montserrat, sans-serif' }} />
              </div>

              {/* Phone */}
              <div>
                <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Phone <span className="normal-case text-[#b0a898] font-normal">(optional)</span>
                </label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className={inputClass} placeholder="(310) 555-0123" style={{ fontFamily: 'Montserrat, sans-serif' }} />
              </div>

              {/* Vehicle of Interest */}
              <div>
                <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Vehicle of Interest <span className="normal-case text-[#b0a898] font-normal">(optional)</span>
                </label>
                <input type="text" name="vehicleInterest" value={formData.vehicleInterest} onChange={handleInputChange} className={inputClass} placeholder="e.g. 2022 BMW X5, pickup truck under $40k..." style={{ fontFamily: 'Montserrat, sans-serif' }} />
              </div>

              {/* Subject */}
              <div>
                <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>Subject</label>
                <div className="relative">
                  <select name="subject" value={formData.subject} onChange={handleInputChange} required className={`${inputClass} appearance-none pr-10 cursor-pointer`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    <option value="">Select a subject</option>
                    <option value="Sales Inquiry">Sales Inquiry</option>
                    <option value="Test Drive Request">Test Drive Request</option>
                    <option value="Financing Question">Financing Question</option>
                    <option value="Service Appointment">Service Appointment</option>
                    <option value="Trade-In Valuation">Trade-In Valuation</option>
                    <option value="General Question">General Question</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-[#b0a898] pointer-events-none text-lg"></i>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className={labelClass} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Message <span className="text-[#b0a898] normal-case font-normal">({formData.message.length}/500)</span>
                </label>
                <textarea name="message" value={formData.message} onChange={handleInputChange} required rows={4} maxLength={500} className={`${inputClass} resize-none`} placeholder="Tell us how we can help..." style={{ fontFamily: 'Montserrat, sans-serif' }} />
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full bg-[#C9A84C] hover:bg-[#b8943e] text-white py-4 rounded-md text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-[1.01] transform disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md min-h-[52px]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {isSubmitting ? <><i className="ri-loader-4-line animate-spin"></i>Sending...</> : <>Send Message <i className="ri-send-plane-line"></i></>}
              </button>
            </form>
          </div>

          {/* Sidebar info */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {/* Business Hours */}
            <div className="bg-white border border-[#e0d8cc] p-5 sm:p-6 rounded-lg shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-time-line text-[#C9A84C] text-base"></i>
                </div>
                <h3 className="text-xs font-bold text-[#1a1a1a] tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Business Hours</h3>
              </div>
              <div className="space-y-2.5">
                {[
                  { day: 'Mon – Fri', hours: '9:00 AM – 7:00 PM' },
                  { day: 'Saturday', hours: '9:00 AM – 6:00 PM' },
                  { day: 'Sunday', hours: '11:00 AM – 5:00 PM' },
                ].map((h) => (
                  <div key={h.day} className="flex justify-between items-center text-xs border-b border-[#e0d8cc] pb-2 last:border-0 last:pb-0" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    <span className="text-[#6b5e4e]">{h.day}</span>
                    <span className="text-[#1a1a1a] font-semibold">{h.hours}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Response promise */}
            <div className="bg-[#fdf8ee] border border-[#C9A84C]/30 p-5 sm:p-6 rounded-lg">
              <div className="flex items-start gap-3">
                <div>
                  <h3 className="text-xs font-bold text-[#1a1a1a] mb-1 tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Our Promise</h3>
                  <p className="text-[#6b5e4e] text-sm leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
                    Every inquiry is handled by a dedicated agent. We respond within 2 hours during business hours — no bots, no runaround.
                  </p>
                </div>
              </div>
            </div>

            {/* Service Area */}
            <div className="bg-[#111111] border border-white/10 p-5 sm:p-6 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-map-pin-2-line text-[#C9A84C] text-base"></i>
                </div>
                <h3 className="text-xs font-bold text-white tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Service Area</h3>
              </div>
              <p className="text-white/50 text-xs leading-relaxed mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Headquartered in <span className="text-[#C9A84C] font-semibold">Miami, FL</span>, we serve customers locally and nationwide with full shipping support.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Miami, FL', 'Fort Lauderdale', 'Boca Raton', 'West Palm Beach', 'Nationwide Shipping'].map((area) => (
                  <span key={area} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] text-white/50 tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>{area}</span>
                ))}
              </div>
            </div>

            {/* CTA to inventory */}
            <Link to="/inventory" className="group bg-white border border-[#e0d8cc] hover:border-[#C9A84C]/50 p-5 sm:p-6 rounded-lg transition-all flex items-center justify-between cursor-pointer shadow-sm">
              <div>
                <p className="text-xs font-bold text-[#1a1a1a] tracking-widest uppercase mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>Browse Inventory</p>
                <p className="text-[11px] text-[#6b5e4e]" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>View available vehicles</p>
              </div>
              <i className="ri-arrow-right-line text-[#C9A84C] text-lg group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Local SEO trust strip */}
      <section className="bg-[#0a0a0a] border-t border-white/5 py-8 sm:py-10 px-4 sm:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6">
            <p className="text-[10px] text-[#C9A84C] tracking-[0.3em] uppercase font-bold mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>Proudly Serving South Florida &amp; Beyond</p>
            <h2 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Miami's Premier Auto Dealer</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: 'ri-car-line', label: 'Premium Inventory', sub: 'Cars, Trucks & SUVs' },
              { icon: 'ri-map-pin-line', label: 'Miami, Florida', sub: 'Serving All of USA' },
              { icon: 'ri-truck-line', label: 'Nationwide Delivery', sub: 'Door-to-door shipping' },
              { icon: 'ri-shield-check-line', label: 'Verified Vehicles', sub: 'Full inspection reports' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center text-center p-4 rounded-lg border border-white/5 bg-white/[0.02]">
                <div className="w-10 h-10 flex items-center justify-center mb-2.5">
                  <i className={`${item.icon} text-[#C9A84C] text-xl`}></i>
                </div>
                <p className="text-white/80 text-xs font-semibold mb-0.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.label}</p>
                <p className="text-white/30 text-[10px]" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#080808] border-t border-white/5 py-8 sm:py-10 lg:py-12 px-4 sm:px-8 lg:px-16">
        <div className="h-px bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent mb-8 sm:mb-10 lg:mb-12"></div>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <img
              src="https://static.readdy.ai/image/41574e0492e980fd88e2c6f8d59234d9/ff8f99c2c3d017ecd9846c9b47aca342.png"
              alt="Paramount Motors Logo"
              style={{ height: '3rem', width: 'auto' }}
              className="object-contain"
            />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            {navItems.map((item) => (
              <Link key={item.name} to={item.path} className="text-xs text-white/40 hover:text-[#C9A84C] transition-colors cursor-pointer tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.name}</Link>
            ))}
          </div>
          <p className="text-xs text-white/20" style={{ fontFamily: 'Montserrat, sans-serif' }}>© 2025 Paramount Motors</p>
        </div>
      </footer>
    </div>
  );
}